/**
 * One book on the shelf, and the cover it gets when it has none.
 *
 * Two decisions worth stating. First, the space for the cover is reserved before
 * the image exists: a grid that reflows as fifty JPEGs land is a grid nobody can
 * click. The frame is a fixed 2:3 box, the image fades into it, and a cover that
 * 404s falls back without the layout moving at all.
 *
 * Second, a book without a cover does not get a grey rectangle. Half a real
 * library has no artwork, and half a shelf of grey rectangles looks broken rather
 * than sparse. The fallback derives a stable hue from the book id and sets the
 * title in the reading serif, so the shelf still reads as a shelf.
 */

import { useState } from "react";

import { href } from "../lib/route";
import { t, useUiLang } from "../i18n";
import type { BookSummary } from "../lib/types";

/**
 * FNV-1a over the id. Any stable hash would do; what matters is that a book
 * keeps its colour between sessions and between machines.
 */
function hueOf(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return Math.abs(hash) % 360;
}

/** Long titles need a smaller face, or they overflow their own cover. */
function titleSize(title: string): string {
  if (title.length > 64) return "clamp(0.62rem, 3.4cqw, 0.95rem)";
  if (title.length > 32) return "clamp(0.72rem, 4.2cqw, 1.15rem)";
  return "clamp(0.85rem, 5.4cqw, 1.5rem)";
}

export function GeneratedCover({ book }: { book: BookSummary }) {
  const hue = hueOf(book.id);
  return (
    <div
      // `container-type` lets the type scale with the card rather than the page,
      // so the same component works in the grid and in the larger Continue row.
      style={{
        containerType: "inline-size",
        background: `linear-gradient(155deg, hsl(${hue} 30% 21%) 0%, hsl(${hue} 26% 13%) 46%, hsl(${(hue + 34) % 360} 24% 9%) 100%)`,
      }}
      className="flex h-full w-full flex-col justify-between overflow-hidden p-[8%]"
    >
      <div
        className="h-px w-8 opacity-60"
        style={{ background: `hsl(${hue} 45% 62%)` }}
        aria-hidden="true"
      />
      <div className="min-h-0">
        <p
          className="line-clamp-5 font-[family-name:var(--font-read)] leading-tight text-[var(--color-ink)]"
          style={{ fontSize: titleSize(book.title) }}
        >
          {book.title}
        </p>
        {book.author && (
          <p
            className="mt-[6%] line-clamp-2 uppercase tracking-[0.14em] text-[var(--color-muted)]"
            style={{ fontSize: "clamp(0.42rem, 2.4cqw, 0.68rem)" }}
          >
            {book.author}
          </p>
        )}
      </div>
      <div
        className="h-px w-full opacity-30"
        style={{ background: `hsl(${hue} 40% 55%)` }}
        aria-hidden="true"
      />
    </div>
  );
}

/** The 2:3 frame. Always present, whatever the cover turns out to be. */
export function Cover({ book, className = "" }: { book: BookSummary; className?: string }) {
  const [state, setState] = useState<"idle" | "shown" | "failed">("idle");
  // The server's own URL, not one rebuilt from the id: it carries the revision
  // of the file, which is the whole reason a cover can be cached for a year.
  const url = book.cover;
  const fallback = !url || state === "failed";

  return (
    <div
      className={
        "relative aspect-[2/3] w-full overflow-hidden rounded-md bg-[var(--color-surface)] " +
        "shadow-[0_1px_2px_rgba(0,0,0,0.5),0_8px_24px_-12px_rgba(0,0,0,0.8)] " +
        className
      }
    >
      {fallback ? (
        <GeneratedCover book={book} />
      ) : (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setState("shown")}
          onError={() => setState("failed")}
          className={
            "h-full w-full object-cover transition-opacity duration-500 [transition-timing-function:var(--ease-out)] " +
            (state === "shown" ? "opacity-100" : "opacity-0")
          }
        />
      )}
      {/* A hairline inside the frame rather than a border around it: it reads as
          printed board, and it costs no layout. */}
      <div
        className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-white/8"
        aria-hidden="true"
      />
    </div>
  );
}

function MarkIcon({ path, filled }: { path: string; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

const AUDIO_PATH = "M4 8v4M7.5 5.5v9M11 7v6M14.5 4v12M18 8.5v3";
const SYNC_PATH = "M3.5 13.5a5 5 0 0 1 5-5h8M16.5 6l2.5 2.5-2.5 2.5M16.5 6.5a5 5 0 0 1-5 5h-8";
const DOC_PATH = "M5 3h6l4 4v10H5zM11 3v4h4";

interface Mark {
  key: string;
  /** Resolved late, in the component, so the mark follows the interface language. */
  label: string;
  node: React.ReactNode;
}

/**
 * What the book has, as marks rather than a table of booleans. `has_sync` implies
 * `has_audio`, so the two never both show: synchronised audio is the stronger
 * claim and it replaces the plain one.
 */
function marksFor(book: BookSummary): Mark[] {
  const marks: Mark[] = [];
  if (book.has_sync) {
    marks.push({
      key: "sync",
      label: t("bookMarkSync"),
      node: <MarkIcon path={SYNC_PATH} />,
    });
  } else if (book.has_audio) {
    marks.push({ key: "audio", label: t("bookMarkAudio"), node: <MarkIcon path={AUDIO_PATH} /> });
  }
  if (book.has_doc) {
    marks.push({ key: "doc", label: t("bookMarkDoc"), node: <MarkIcon path={DOC_PATH} /> });
  }
  return marks;
}

export function Marks({ book }: { book: BookSummary }) {
  useUiLang();
  const marks = marksFor(book);
  return (
    <div className="flex items-center gap-1.5 text-[var(--color-faint)]">
      <span
        className="font-[family-name:var(--font-mono)] text-2xs uppercase tracking-wider"
        title={t("bookLangTitle", { lang: book.lang })}
      >
        {book.lang}
      </span>
      {marks.map((mark) => (
        <span key={mark.key} title={mark.label} aria-label={mark.label} role="img">
          {mark.node}
        </span>
      ))}
    </div>
  );
}

/** Minutes left, from the audio when there is any, from the word count otherwise. */
export function remainingLabel(book: BookSummary): string | null {
  const percent = book.progress?.percent ?? 0;
  if (book.duration && book.progress) {
    const left = Math.max(0, book.duration - book.progress.seconds);
    return t("bookLeft", { time: formatMinutes(left / 60) });
  }
  if (book.words > 0) {
    // 230 words a minute is the usual figure for silent reading of prose.
    const left = (book.words * (1 - percent)) / 230;
    return t("bookLeft", { time: formatMinutes(left) });
  }
  return null;
}

function formatMinutes(minutes: number): string {
  if (minutes < 1) return t("bookUnderMinute");
  if (minutes < 60) return t("bookMinutes", { n: Math.round(minutes) });
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  // Two digits, in both languages. French drops the unit after the minutes, so
  // `3 h 8` reads as a broken clock rather than a duration; English keeps it,
  // and `3 h 08 min` is no worse for the padding. One form is worth more than
  // the half hour it would take to justify two.
  return rest > 0
    ? t("bookHoursMinutes", { h: hours, m: String(rest).padStart(2, "0") })
    : t("bookHours", { h: hours });
}

/**
 * Where the reader stands, as one of three states.
 *
 * 0.98 mirrors `FINISHED` in indexer.py, which is what the `status` filter uses
 * on the server. The two have to agree: a card badged "read" that the Finished
 * chip then refuses to show is a card that looks like a bug.
 */
export type BookStatus = "finished" | "reading" | "unread";

const FINISHED = 0.98;

export function statusOf(book: BookSummary): BookStatus {
  if (!book.progress) return "unread";
  return book.progress.percent >= FINISHED ? "finished" : "reading";
}

export function CheckIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M4.5 10.5l3.6 3.5L15.5 6.5" />
    </svg>
  );
}

/**
 * The share read, as a ring. It sits on the cover, where a percentage in words
 * would be unreadable at this size and a bar would fight the artwork. Unread
 * books get nothing at all: an empty ring on two thirds of a shelf is noise.
 */
export function ProgressRing({ percent, className = "" }: { percent: number; className?: string }) {
  const value = Math.min(1, Math.max(0, percent));
  const circumference = 2 * Math.PI * 7;
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="7" fill="none" stroke="var(--color-line)" strokeWidth="2.6" />
      <circle
        cx="10"
        cy="10"
        r="7"
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeDasharray={`${circumference * value} ${circumference}`}
        transform="rotate(-90 10 10)"
      />
    </svg>
  );
}

/**
 * The one glance that answers "have I read this". A filled tick for a finished
 * book, a ring for one under way, nothing for one never opened.
 */
export function StatusBadge({ book, className = "" }: { book: BookSummary; className?: string }) {
  useUiLang();
  const status = statusOf(book);
  if (status === "unread") return null;
  const percent = Math.round((book.progress?.percent ?? 0) * 100);
  const label = status === "finished" ? t("bookFinished") : t("bookPercentRead", { n: percent });
  return (
    <span
      title={label}
      aria-label={label}
      role="img"
      className={
        "inline-flex size-fib21 items-center justify-center rounded-full " +
        "bg-[var(--color-page)]/85 ring-1 ring-inset ring-[var(--color-line)] backdrop-blur-sm " +
        className
      }
    >
      {status === "finished" ? (
        <span className="inline-flex size-fib13 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-on-accent)]">
          <CheckIcon className="h-2.5 w-2.5" />
        </span>
      ) : (
        <ProgressRing percent={book.progress?.percent ?? 0} className="size-fib13" />
      )}
    </span>
  );
}

/**
 * The same answer in words, for the line under a title and for the list rows.
 * Null for a book nobody has opened, which is what "nothing to say" looks like.
 */
export function progressLabel(book: BookSummary): string | null {
  const status = statusOf(book);
  if (status === "unread") return null;
  if (status === "finished") return t("bookFinished");
  const percent = Math.round((book.progress?.percent ?? 0) * 100);
  const left = remainingLabel(book);
  return left ? `${t("bookPercent", { n: percent })} · ${left}` : t("bookPercent", { n: percent });
}

export function ProgressBar({ percent }: { percent: number }) {
  useUiLang();
  const value = Math.round(Math.min(1, Math.max(0, percent)) * 100);
  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t("bookPercentRead", { n: value })}
      className="h-fib3 w-full overflow-hidden rounded-full bg-[var(--color-line)]"
    >
      <div
        className="h-full rounded-full bg-[var(--color-accent)] transition-[width] duration-500"
        // A book at 0 gets no bar at all. The list view draws an empty track for
        // every unread book, and a 1% dot on each of them would read as "started".
        style={{ width: `${value === 0 ? 0 : Math.max(value, 1)}%` }}
      />
    </div>
  );
}

export function BookCard({ book }: { book: BookSummary }) {
  useUiLang();
  const percent = book.progress?.percent ?? 0;
  const progress = progressLabel(book);
  const finished = statusOf(book) === "finished";
  return (
    <a
      href={href({ name: "book", id: book.id })}
      // The accessible name has to carry the author too: a screen reader user
      // hearing forty links called "Chapter" learns nothing.
      aria-label={
        book.author ? t("bookByAuthor", { title: book.title, author: book.author }) : book.title
      }
      className="group flex flex-col gap-2.5 rounded-lg outline-offset-4"
    >
      <div className="relative transition-transform duration-300 [transition-timing-function:var(--ease-out)] group-hover:-translate-y-1">
        <Cover book={book} />
        <div className="pointer-events-none absolute inset-0 rounded-md bg-[var(--color-accent)] opacity-0 mix-blend-overlay transition-opacity duration-300 group-hover:opacity-10" />
        {/* On the cover rather than under the title: the question "have I read
            this" is asked of the shelf as a whole, by sweeping the eye across
            the artwork, and an answer that only exists in the caption is an
            answer nobody sweeps. */}
        <StatusBadge book={book} className="absolute right-1.5 top-1.5" />
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-[var(--color-ink)] transition-colors duration-150 group-hover:text-[var(--color-accent)]">
          {book.title}
        </h3>
        {book.author && (
          <p className="line-clamp-1 text-xs text-[var(--color-muted)]">{book.author}</p>
        )}
        {percent > 0 && <ProgressBar percent={percent} />}
        <div className="flex items-center justify-between gap-2">
          <Marks book={book} />
          {progress && (
            <span
              className={
                "shrink-0 truncate text-2xs " +
                (finished ? "text-[var(--color-accent)]" : "text-[var(--color-faint)]")
              }
            >
              {progress}
            </span>
          )}
        </div>
      </div>
    </a>
  );
}

/** Same footprint as a card, so the grid does not move when the books arrive. */
export function BookCardSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-2.5" aria-hidden="true">
      <div className="aspect-[2/3] w-full rounded-md bg-[var(--color-surface)]" />
      <div className="h-3 w-4/5 rounded bg-[var(--color-surface)]" />
      <div className="h-2.5 w-1/2 rounded bg-[var(--color-surface)]" />
    </div>
  );
}
